<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="600px"
  >
    <template slot="dialogTitle">
      <span v-if="financeModel.id">
        Edit Finance Model
      </span>
      <span v-else>
        Add New Finance Model
      </span>
    </template>

    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-autocomplete
        v-model="financeModelDirty.country_id"
        v-validate="'required'"
        :items="countries.data"
        :loading="countries.loading"
        name="country"
        item-value="id"
        item-text="name"
        label="Country"
        hint="Select country this financing model is applicable to"
        autocomplete="disable"
        outlined
        :error-messages="veeErrors.collect('country')"
      />
      <v-text-field
        v-model="financeModelDirty.label"
        v-validate="'required'"
        name="label"
        label="Label"
        placeholder="for e.g. Smart Low (OTP Leasing)"
        autocomplete="disable"
        outlined
        :error-messages="veeErrors.collect('label')"
      />
      <v-select
        v-model="selectedFinanceModelType"
        v-validate="'required'"
        name="financingModelType"
        item-text="name"
        label="Financing Model Type"
        autocomplete="disable"
        :items="financeModelTypes.data"
        :loading="financeModelTypes.loading"
        return-object
        outlined
        data-vv-as="financing model type"
        :error-messages="veeErrors.collect('financingModelType')"
      />
      <div v-if="selectedFinanceModelType">
        <v-subheader>
          Specify required parameters for the selected financing model type
        </v-subheader>

        <v-text-field
          v-for="parameter in selectedFinanceModelType.parameters"
          :key="parameter.field"
          v-model="parameter.value"
          v-validate="'required|decimal'"
          :label="parameter.label"
          :suffix="parameter.unit"
          :name="parameter.field"
          :data-vv-as="parameter.label"
          :error-messages="veeErrors.collect(parameter.field)"
          outlined
        />

        <v-subheader>
          Yacht income settings
        </v-subheader>

        <v-text-field
          v-model="incomeSafetyFactor"
          v-validate="'required|decimal'"
          name="income_safety_factor"
          data-vv-as="income safety factor"
          label="Minimum Income Safety Margin"
          suffix="%"
          outlined
          type="number"
          :error-messages="veeErrors.collect('income_safety_factor')"
        />

        <v-row>
          <v-col
            v-for="year in yearsCount"
            :key="'years_factor_' + year"
            style="flex: 33.3% 0 0"
          >
            <v-text-field
              v-model="financeModelDirty.yearly_income_factors[year - 1]"
              v-validate="'required|numeric'"
              suffix="%"
              outlined
              type="number"
              :label="'Income Year ' + year"
              :data-vv-as="'income year ' + year"
              :name="'income_year_' + year"
              :error-messages="veeErrors.collect('income_year_' + year)"
            />
          </v-col>
        </v-row>

        <v-subheader>
          Financing Institution (optional)
        </v-subheader>

        <v-text-field
          v-model="financeModelDirty.institution_name"
          label="Financing Institution Name"
          outlined
        />

        <v-subheader>
          Add a note (optional)
        </v-subheader>

        <v-textarea
          v-model="financeModelDirty.note"
          label="Note about the financing model"
          outlined
        />
      </div>
    </v-card-text>

    <template slot="dialogActions">
      <v-btn
        v-if="financeModelDirty.id && $can('delete', 'FinanceModel')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Finance Model?"
      @confirmed="deleteFinanceModel"
    />
  </content-dialog>
</template>

<script>
  import cloneDeep from 'lodash/cloneDeep'
  import { mapState } from 'vuex'
  import FinanceModel from '@/models/FinanceModel'
  import ContentDialog from '@/components/helpers/Dialog'
  import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
  import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
  import Big from 'big.js'

  export default {
    components: {
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      financeModel: {
        type: Object,
        default: () => FinanceModel.emptyModel
      },
      financeModelTypes: {
        type: Object,
        default: () => {
          return {
            data: [],
            loading: false
          }
        }
      }
    },
    data() {
      return {
        financeModelDirty: cloneDeep(FinanceModel.emptyModel),
        selectedFinanceModelType: null,
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      yearsCount() {
        if (!this.selectedFinanceModelType) return 0

        const monthsParam = this.selectedFinanceModelType.parameters.find(p => p.field === 'investmentPeriodMonths')

        if (!monthsParam || !monthsParam.value) return 0

        return Math.ceil(monthsParam.value / 12)
      },
      incomeSafetyFactor: {
        get: function () {
          if (!this.financeModelDirty.income_safety_factor) return null

          return Big(this.financeModelDirty.income_safety_factor).times(100).round(2).toString()
        },
        set: function (value) {
          if (!value) {
            this.financeModelDirty.income_safety_factor = null
            return
          }

          this.financeModelDirty.income_safety_factor = Big(value).div(100).round(4).toString()
        }
      },
      ...mapState('settings', ['countries'])
    },
    watch: {
      financeModel() {
        this.financeModelDirty = cloneDeep(this.financeModel)

        this.selectedFinanceModelType = this.financeModelDirty.specifications
        this.selectedFinanceModelType.parameters = this.financeModelDirty.parameter_values

        if (!this.financeModelDirty.yearly_income_factors) {
          this.financeModelDirty.yearly_income_factors = []
          return
        }

        this.financeModelDirty.yearly_income_factors = this.financeModelDirty.yearly_income_factors.map((factor, key) => {
          return Big(factor).times(100).round(2).toString()
        })
      }
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validateAll()

        if (!validation) return

        this.store()
      },
      async store() {
        this.submitting = true

        this.financeModelDirty.type = this.selectedFinanceModelType.type
        this.financeModelDirty.parameters = this.getParameterValues()

        let data = {
          label: this.financeModelDirty.label,
          type: this.selectedFinanceModelType.type,
          country_id: this.financeModelDirty.country_id,
          parameters: this.getParameterValues() || [],
          income_safety_factor: this.financeModelDirty.income_safety_factor,
          yearly_income_factors: this.financeModelDirty.yearly_income_factors.map((factor, key) => {
            return Big(factor).div(100).round(4).toString()
          }).slice(0, this.yearsCount),
          institution_name: this.financeModelDirty.institution_name,
          note: this.financeModelDirty.note
        }

        try {
          let response = await FinanceModel.api.save(data, this.financeModelDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Financing model successfully saved!',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.financeModelDirty.id ? 'financeModel:updated' : 'financeModel:created', response.financeModel)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': e.response.data.message,
            'color': 'error'
          })
        } finally {
          this.submitting = false
        }
      },
      async deleteFinanceModel() {
        this.deleting = true

        try {
          await FinanceModel.api.destroy(this.financeModelDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Finance Model deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('financeModel:deleted', this.financeModelDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      },
      getParameterValues() {
        let params = this.selectedFinanceModelType.parameters.filter(p => !!p.value)

        let paramValues = {}

        for (let param of params) paramValues[param.field] = param.value

        return paramValues
      }
    }
  }
</script>
